import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Modal,
  Row,
  Col,
  message,
} from 'antd';
import { postSolicitarActualizarDatosDeContacto } from 'api/padrones';
import LadaNumero from 'components/LadaNumero';
import _logger from 'utils/logger';
import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Text,
  Title,
} from 'components';
import { Turnstile } from '@marsidev/react-turnstile';
import { siteKey } from 'utils/env';
import { NO_PLUS_EMAIL_RULE } from 'utils/patterns';

const ModalUpdateInfo = ({
  visible,
  onCancel,
  setVisible,
  idPadron,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const turnstileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState();

  const handleOk = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const _values = {
        correo_electronico: values.correo_electronico,
        telefono_celular: values.telefono_celular,
        lada_celular: values.lada_celular,
      };
      const sent = await postSolicitarActualizarDatosDeContacto({
        ..._values,
        ...initialValues,
        cf_turnstile_token: token,
      });
      if (sent) {
        setVisible(false);
        message.info(sent.detail || 'Se enviaron las instrucciones para actualizar los datos de contacto.');
      }
    } catch (err) {
      _logger(err);
    } finally {
      turnstileRef.current?.reset();
      setToken(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    form.setFieldsValue({
      id: idPadron,
      lada_celular: 34,
    });
    setToken(null);
    turnstileRef.current?.reset();
  }, [idPadron, visible]);

  return (
    <Modal
      title={(<Title level={3}>Enviar instrucciones de actualización</Title>)}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Enviar instrucciones"
      cancelText="Cancelar"
      maskClosable={false}
      centered
      closable={false}
      confirmLoading={loading}
      footer={(
        <Row justify="end" gutter={[10, 15]}>
          <Col xs={24} sm={24} md={8}>
            <Button
              onClick={onCancel}
              icon={<ArrowLeftOutlined />}
              disabled={loading}
              style={{ width: '100%' }}
            >
              Cancelar
            </Button>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Button
              type="primary"
              style={{ width: '100%' }}
              onClick={handleOk}
              loading={loading}
              disabled={!token}
              icon={<MailOutlined />}
              $accent
            >
              Enviar correo
            </Button>
          </Col>
        </Row>
      )}
    >
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            onFinish={token ? handleOk : undefined}
            initialValues={initialValues}
            preserve={false}
          >
            <Col span={24}>
              <Card>
                <Text style={{ textAlign: 'center' }}>
                  El contribuyente no tiene información de contacto registrada o está
                  desactualizada. Ingresa un correo electrónico y un teléfono celular para
                  enviarle las instrucciones y el enlace donde podrá confirmar y actualizar
                  sus datos de contacto.
                </Text>
              </Card>
            </Col>
            <br />
            <Col span={24}>
              <Form.Item
                rules={[{ required: true }, { type: 'email' }, NO_PLUS_EMAIL_RULE]}
                label="Correo electrónico"
                name="correo_electronico"
                extra="Se enviarán las instrucciones a este correo."
              >
                <Input placeholder="Ingrese el correo electrónico del contribuyente" />
              </Form.Item>
            </Col>
            <LadaNumero
              form={form}
              names={{
                lada: 'lada_celular',
                numero_de_telefono: 'telefono_celular',
              }}
              label="Teléfono"
              hasFeedback
              fullSize
            />
            <Form.Item name="id" hidden>
              <Input disabled />
            </Form.Item>
            {visible && (
              <Form.Item noStyle>
                <Turnstile
                  siteKey={siteKey}
                  onSuccess={(cf_turnstile_token) => setToken(cf_turnstile_token)}
                  onError={() => setToken(null)}
                  onExpire={() => setToken(null)}
                  ref={turnstileRef}
                />
              </Form.Item>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

ModalUpdateInfo.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  setVisible: PropTypes.func.isRequired,
  idPadron: PropTypes.number.isRequired,
  initialValues: PropTypes.shape({
    q: PropTypes.string,
    correo_electronico: PropTypes.string,
    telefono_celular: PropTypes.number,
    lada_celular: PropTypes.number,
  }),
};

ModalUpdateInfo.defaultProps = {
  initialValues: {
    correo_electronico: '',
    telefono_celular: 0,
    q: null,
  },
};

export default ModalUpdateInfo;
